// =============================================================================
// Форматтеры денег и времени для UI.
// =============================================================================

/** $50.00 micro → "$50" / "$50.50" / "$1,234" */
export function formatUsdt(microUnits: number): string {
  const usd = microUnits / 1_000_000;
  if (usd >= 1000) return `$${Math.round(usd).toLocaleString('en-US')}`;
  if (Number.isInteger(usd)) return `$${usd}`;
  return `$${usd.toFixed(2)}`;
}

/** "USDT" / "TON" в человеческом формате */
export function formatAsset(asset: string): string {
  switch (asset) {
    case 'USDT_TRC20':
      return 'USDT (TRC-20)';
    case 'USDT_TON':
      return 'USDT (TON)';
    case 'TON':
      return 'TON';
    default:
      return asset;
  }
}

/** "5d 12h" / "3h 20m" / "12m" / "<1m" — до закрытия рынка */
export function formatTimeUntil(closeAtSec: number, nowSec?: number): string {
  const now = nowSec ?? Math.floor(Date.now() / 1000);
  const delta = closeAtSec - now;
  if (delta <= 0) return 'closed';
  const days = Math.floor(delta / 86400);
  const hours = Math.floor((delta % 86400) / 3600);
  const mins = Math.floor((delta % 3600) / 60);
  if (days >= 1) return `${days}d ${hours}h`;
  if (hours >= 1) return `${hours}h ${mins}m`;
  if (mins >= 1) return `${mins}m`;
  return '<1m';
}

/** Доля YES в процентах от общего пула. */
export function yesPercent(poolYesMicro: number, poolNoMicro: number): number {
  const total = poolYesMicro + poolNoMicro;
  if (total === 0) return 50;
  return Math.round((poolYesMicro / total) * 100);
}

/** "elections" → "Выборы", и так далее */
export function categoryLabel(category: string): string {
  const map: Record<string, string> = {
    elections: 'Выборы',
    conflicts: 'Конфликты',
    diplomacy: 'Дипломатия',
    sanctions: 'Санкции',
    domestic: 'Политика',
    geopolitics: 'Геополитика',
  };
  return map[category] ?? category;
}

/** "только что" / "5 мин назад" / "2ч назад" / "вчера" / "12 апр" / "12 апр 2025" */
export function formatRelative(unixSec: number, nowSec?: number): string {
  const now = nowSec ?? Math.floor(Date.now() / 1000);
  const delta = Math.max(0, now - unixSec);
  if (delta < 60) return 'только что';
  if (delta < 3600) return `${Math.floor(delta / 60)} мин назад`;
  if (delta < 86400) return `${Math.floor(delta / 3600)}ч назад`;
  if (delta < 86400 * 2) return 'вчера';
  if (delta < 86400 * 7) return `${Math.floor(delta / 86400)}д назад`;
  const date = new Date(unixSec * 1000);
  const months = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  const currentYear = new Date(now * 1000).getFullYear();
  return year === currentYear ? `${day} ${month}` : `${day} ${month} ${year}`;
}

/** "12.04.2025 14:32" — для подробной даты */
export function formatDateTime(unixSec: number): string {
  const d = new Date(unixSec * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Русская плюрализация: 1 ставка, 2 ставки, 5 ставок. */
export function pluralizeRu(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}

/**
 * Реальный текущий estimated payout для активной ставки.
 * Использует ту же математику что engine.estimatePayout, но снаружи.
 */
export function liveEstimateNetWinningsMicro(
  amountMicro: number,
  side: number,
  poolYesAtBet: number,
  poolNoAtBet: number,
  currentPoolYes: number,
  currentPoolNo: number,
  feeBps: number,
): number {
  // Pool на собственной стороне НА МОМЕНТ резолюции (если бы сейчас) уже включает наш bet.
  const ownPool = side === 1 ? currentPoolYes : currentPoolNo;
  const oppositePool = side === 1 ? currentPoolNo : currentPoolYes;
  if (oppositePool === 0 || ownPool === 0) return 0;
  const fee = Math.floor((oppositePool * feeBps) / 10_000);
  const distributable = oppositePool - fee;
  // share = ourStake / ownPool. Но ownPool УЖЕ включает наш стейк (current).
  // Поэтому правильно: amountMicro / ownPool.
  return Math.floor((distributable * amountMicro) / ownPool);
}

/** Скрываем полное имя для privacy в публичных feed'ах: только первая буква + '…'. */
export function anonymize(handle: string): string {
  if (handle.startsWith('@')) {
    if (handle.length <= 4) return handle;
    return handle.slice(0, 3) + '…' + handle.slice(-1);
  }
  if (handle.length <= 2) return handle;
  return handle[0]! + '…';
}
